import { useCallback } from 'react';
import type { FileRejection } from 'react-dropzone';
import { useDropzone } from 'react-dropzone';
import { Button } from '@archipro/rocco/components/button';
import { PaperclipLight, TimesLight } from '@archipro-design/icons';
import {
    CONSTANTS,
    MIME_TYPE_MAPPING,
    truncateFilename,
} from '~/utils/enquiry-form-helper';
import { useToaster } from '~/modules/root/context/ToasterContext';

interface FileAttachmentProps {
    maxAttachments?: number;
    files: File[];
    onChange: (files: File[]) => void;
    disabled?: boolean;
}

const FileList = ({
    files,
    onChange,
    disabled,
}: {
    files: File[];
    onChange: (files: File[]) => void;
    disabled: boolean;
}) => (
    <div className="mt-2 flex flex-wrap gap-2">
        {files.map((file, index) => (
            <div
                key={index}
                className="mt-2 flex items-center gap-2 rounded-md bg-gray-100 px-3 py-1.5 text-sm"
            >
                <span>{truncateFilename(file.name)}</span>
                <button
                    type="button"
                    onClick={() =>
                        onChange(files.filter((_, i) => i !== index))
                    }
                    disabled={disabled}
                    className={`text-gray-500 hover:text-gray-700 ${
                        disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    }`}
                >
                    <TimesLight className="h-4 w-4" />
                </button>
            </div>
        ))}
    </div>
);

export const FileAttachment = ({
    maxAttachments = 5,
    files,
    onChange,
    disabled = false,
}: FileAttachmentProps) => {
    const { toast } = useToaster();

    const onFileDrop = useCallback(
        (acceptedFiles: File[], fileRejections: FileRejection[]) => {
            const totalFiles = files.length + acceptedFiles.length;
            if (totalFiles > maxAttachments) {
                toast(
                    `Maximum file count reached. You can upload ${maxAttachments} files at once`,
                    {
                        type: 'error',
                    },
                );
                return;
            }

            // Handle rejected files
            if (fileRejections.length > 0) {
                const sizeRejections = fileRejections.filter((rejection) =>
                    rejection.errors.some(
                        (error) => error.code === 'file-too-large',
                    ),
                );
                const typeRejections = fileRejections.filter((rejection) =>
                    rejection.errors.some(
                        (error) => error.code === 'file-invalid-type',
                    ),
                );

                if (sizeRejections.length > 0) {
                    const fileNames = sizeRejections
                        .map((rejection) => rejection.file.name)
                        .join(', ');
                    toast(
                        `The following files exceed the maximum size limit of ${CONSTANTS.MAX_FILE_SIZE / (1024 * 1024)}MB: ${fileNames}`,
                        {
                            type: 'error',
                        },
                    );
                }

                if (typeRejections.length > 0) {
                    const fileNames = typeRejections
                        .map((rejection) => rejection.file.name)
                        .join(', ');
                    toast(
                        `The following files have unsupported formats: ${fileNames}.`,
                        {
                            type: 'error',
                        },
                    );
                }

                return;
            }

            // --- Extension-based validation for Safari and robustness ---
            const allowedExtensions = CONSTANTS.ALLOWED_FILES_ENQUIRY_INBOX.map(
                (ext) => ext.toLowerCase(),
            );
            const invalidExtensionFiles = acceptedFiles.filter((file) => {
                if (!file.name || typeof file.name !== 'string') return true;
                const extMatch = file.name?.match(/\.([^.]+)$/);
                if (!extMatch || !extMatch[1]) return true; // No extension
                const ext = extMatch[1].toLowerCase();
                return !allowedExtensions.includes(ext);
            });
            if (invalidExtensionFiles.length > 0) {
                const fileNames = invalidExtensionFiles
                    .map((f) => f.name)
                    .join(', ');
                toast(
                    `The following files have unsupported extensions: ${fileNames}.`,
                    {
                        type: 'error',
                    },
                );
                return;
            }
            // --- End extension-based validation ---

            // If we have valid files, add them
            if (acceptedFiles.length > 0) {
                const mergedFiles = [...files, ...acceptedFiles];
                onChange(mergedFiles);
            }
        },
        [files, maxAttachments, onChange, toast],
    );

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: onFileDrop,
        multiple: true,
        disabled: files.length >= maxAttachments,
        noClick: files.length >= maxAttachments,
        noDrag: files.length >= maxAttachments,
        noKeyboard: files.length >= maxAttachments,
        accept: CONSTANTS.ALLOWED_FILES_ENQUIRY_INBOX.reduce(
            (acc, ext) => {
                const mimeTypes = MIME_TYPE_MAPPING[ext.toLowerCase()] || [
                    'application/octet-stream',
                ];
                mimeTypes.forEach((mimeType) => {
                    Object.assign(acc, {
                        [mimeType]: [...(acc[mimeType] || []), `.${ext}`],
                    });
                });
                return acc;
            },
            {} as { [key: string]: string[] },
        ),
        maxSize: CONSTANTS.MAX_FILE_SIZE,
    });

    return (
        <div>
            <div
                {...getRootProps()}
                className={
                    disabled || files.length >= maxAttachments
                        ? 'pointer-events-none'
                        : ''
                }
            >
                <input {...getInputProps()} />
                <Button
                    type="button"
                    size="larger"
                    className="flex items-center gap-2 border border-gray-200 bg-gray-100 hover:border-gray-300 hover:bg-gray-200"
                    disabled={disabled || files.length >= maxAttachments}
                >
                    Attach files
                    <PaperclipLight className="h-4 w-4" />
                </Button>
            </div>
            <FileList files={files} onChange={onChange} disabled={disabled} />
        </div>
    );
};
